import { BaseDialog } from '@/components/BaseDialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useCreateQuotation, useGetQuotations } from '@/features/quotations/hooks/useQuotation'
import { useGetVendors } from '@/features/vendors/hooks/useVendors'
import { getMutationOptions } from '@/utils/mutation'
import { Check, Copy, File, Link2, Send } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function RequestQuotationDialog({
  open,
  onOpenChange,
  productId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  productId: string
}) {
  const { t } = useTranslation()
  const [selectedVendorId, setSelectedVendorId] = useState('')

  const createQuotation = useCreateQuotation()
  const { data: vendors = [], isFetching: isFetchingVendor } = useGetVendors(undefined, open)
  const { data: quotations = [], isFetching: isFetchingQuote } = useGetQuotations(
    { productId: productId, status: 'Pending' },
    !!productId && open,
  )

  useEffect(() => {
    if (!open) return
    setCreatedRequest(null)
    setSelectedVendorId('')
    setNote('')
    setCopied(false)
    if (copyTimeout) {
      clearTimeout(copyTimeout)
    }
  }, [open])

  const [note, setNote] = useState('')
  const [createdRequest, setCreatedRequest] = useState<{ id: string; vendorName: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const [copyTimeout, setCopyTimeout] = useState<number | null>(null)

  async function handleSubmit(vendorId: string) {
    if (!vendorId) return

    const options = getMutationOptions({
      successMessage: t('quotation.toast.requestSuccess'),
    })

    const payload = {
      productId,
      vendorId: vendorId,
      adminNote: note,
    }

    const req = await createQuotation.mutateAsync({ payload }, options)
    const vendor = vendors.find(v => v.id === vendorId)
    setCreatedRequest({ id: req.id, vendorName: vendor?.name || '' })
    setSelectedVendorId('')
    setNote('')
  }

  function handleCopyLink(quotationId: string) {
    if (!quotationId) return
    const link = `${window.location.origin}/quote/${quotationId}`
    navigator.clipboard.writeText(link)

    setCopied(true)
    if (copyTimeout) {
      clearTimeout(copyTimeout)
    }

    const timeout = window.setTimeout(() => {
      setCopied(false)
    }, 2000)
    setCopyTimeout(timeout)
  }

  function handleClose() {
    if (copyTimeout) {
      clearTimeout(copyTimeout)
    }
    setCreatedRequest(null)
    setCopied(false)
    onOpenChange(false)
  }

  const pendingQuotation = quotations.find(q => q.vendorId === selectedVendorId)

  const canRequest = !!selectedVendorId && !pendingQuotation
  return (
    <BaseDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('quotation.requestDialog.title')}
      contentTestId="quotation-request-dialog"
      isLoading={isFetchingQuote || isFetchingVendor}
      footer={
        createdRequest ? (
          <Button data-testid="quotation-request-finish" onClick={handleClose} className="text-xs">
            {t('common.finish')}
          </Button>
        ) : (
          <>
            <Button data-testid="quotation-request-cancel" variant="outline" onClick={() => onOpenChange(false)} className="text-xs">
              {t('common.cancel')}
            </Button>

            <Button data-testid="quotation-request-create" onClick={() => handleSubmit(selectedVendorId)} className="text-xs" disabled={!canRequest}>
              <Send className="mr-1 size-3" />
              {t('quotation.requestDialog.createButton')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col w-full sm:w-1/2 gap-1.5">
          <Label className="text-xs">{t('common.placeholder.selectVendor')} *</Label>
          <Select
            value={selectedVendorId}
            disabled={isFetchingVendor}
            onValueChange={value => {
              setSelectedVendorId(value)
              setCopied(false)
            }}
          >
            <SelectTrigger data-testid="quotation-request-vendor-trigger" className="h-9 bg-secondary/50 text-xs w-full">
              <SelectValue placeholder={t('common.placeholder.selectVendor')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              {vendors.length > 0 ? (
                vendors.map(v => (
                  <SelectItem key={v.id} value={v.id} data-testid={`quotation-request-vendor-option-${v.id}`}>
                    {v.name}
                  </SelectItem>
                ))
              ) : (
                <div className="p-2 text-xs text-muted-foreground">
                  {t('quotation.requestDialog.allVendorRequested')}
                </div>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          {selectedVendorId && (
            <div>
              {!pendingQuotation && (
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs"> {t('quotation.requestDialog.noteLabel')}</Label>
                  <Textarea
                    data-testid="quotation-request-note-input"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    className="bg-secondary/50 text-xs"
                    rows={2}
                    placeholder={t('quotation.requestDialog.notePlaceholder')}
                  />
                </div>
              )}

              {/* {isSubmittedAndValid && (
                <div className="flex flex-col items-center gap-3 rounded-lg border  border-success/30 bg-success/10 p-4">
                  <FileCheck className="size-8 text-success" />
                  <p className="text-sm font-medium text-foreground"> {t('quotation.requestDialog.submitted.title')}</p>
                </div>
              )} */}

              {pendingQuotation && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4">
                    <File className="size-8 text-primary" />
                    <p className="text-sm font-medium text-foreground"> {t('quotation.requestDialog.pending.title')}</p>
                  </div>
                  <div className="flex items-center gap-2 rounded-lg border border-border/30 bg-secondary/50 p-3">
                    <Link2 className="size-4 shrink-0 text-primary" />
                    <code className="flex-1 truncate text-xs text-foreground">
                      {window.location.origin}/quote/{pendingQuotation.id}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleCopyLink(pendingQuotation.id)}
                    >
                      {copied ? <Check className="mr-1 size-3 text-success" /> : <Copy className="mr-1 size-3" />}
                      {copied ? t('common.copy.success') : t('common.copy.default')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {createdRequest && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-center gap-3 rounded-lg border border-success/30 bg-success/10 p-4">
              <Check className="size-8 text-success" />
              <p className="text-sm font-medium text-foreground"> {t('quotation.requestDialog.created.title')}</p>
              <p className="text-center text-xs text-muted-foreground">
                {t('quotation.requestDialog.created.description', {
                  vendorName: createdRequest.vendorName,
                })}
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-border/30 bg-secondary/50 p-3">
              <Link2 className="size-4 shrink-0 text-primary" />
              <code data-testid="quotation-request-created-link" className="flex-1 truncate text-xs text-foreground">
                {window.location.origin}/quote/{createdRequest.id}
              </code>
              <Button
                data-testid="quotation-request-copy-link"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleCopyLink(createdRequest.id)}
              >
                {copied ? <Check className="mr-1 size-3 text-success" /> : <Copy className="mr-1 size-3" />}
                {copied ? t('common.copy.success') : t('common.copy.default')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </BaseDialog>
  )
}
